import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import styled from "styled-components";

const ResetPasswordCustomisedScreen = () => {
    const navigate = useNavigate();
    const auth = getAuth();

    const [oobCode, setOobCode] = useState(null);
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [stage, setStage] = useState("loading"); // loading | form | success | error
    const [errorMsg, setErrorMsg] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showTextLogo, setShowTextLogo] = useState(false);

    useEffect(() => {
        const tenantId = process.env.REACT_APP_AUTH_TENANT_ID_REF;
        if (tenantId) auth.tenantId = tenantId;

        const params = new URLSearchParams(window.location.search);
        const code = params.get("oobCode");
        const mode = params.get("mode");

        const isResetMode = mode === "resetPassword" || mode === "action";

        if (!isResetMode || !code) {
            setErrorMsg("Ungültiger oder fehlerhafter Passwort-Zurücksetzungslink.");
            setStage("error");
            return;
        }

        setOobCode(code);

        verifyPasswordResetCode(auth, code)
            .then((userEmail) => {
                setEmail(userEmail);
                setStage("form");
            })
            .catch(() => {
                setErrorMsg("Dieser Passwort-Zurücksetzungslink ist ungültig oder abgelaufen.");
                setStage("error");
            });
    }, [auth]);

    const validateRequirements = (pw) => {
        return {
            length: pw.length >= 12,
            upper: /[A-Z]/.test(pw),
            number: /\d/.test(pw),
            special: /[!@#$%^&*()_\-+=\[\]{};:'"\\|,.<>\/?`~]/.test(pw),
        };
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setErrorMsg("");

        if (newPassword.length < 12) {
            setErrorMsg("Das Passwort muss mindestens 12 Zeichen lang sein.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMsg("Die Passwörter stimmen nicht überein.");
            return;
        }

        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            setStage("success");
            setTimeout(() => navigate("/login"), 3000);
        } catch (error) {
            if (error.code === "auth/weak-password") {
                setErrorMsg("Das Passwort ist zu schwach.");
            } else if (error.code === "auth/expired-action-code") {
                setErrorMsg("Dieser Zurücksetzungslink ist abgelaufen.");
            } else {
                setErrorMsg("Passwort konnte nicht zurückgesetzt werden. Bitte erneut versuchen.");
            }
        }
    };

    const req = validateRequirements(newPassword);

    if (stage === "loading") return <div className="text-center mt-5">Überprüfe Zurücksetzungslink…</div>;
    if (stage === "error") return <div className="text-center mt-5 text-danger">{errorMsg}</div>;
    if (stage === "success")
        return <div className="text-center mt-5">Passwort erfolgreich geändert. Weiterleitung zum Login…</div>;

    const logoSrc = "/logo.png";

    return (
        <PageWrapper>
            <CardWrapper className="card shadow-sm">
                <Header>
                    <Logo
                        src={logoSrc}
                        alt="Pathways"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                            setShowTextLogo(true);
                        }}
                    />
                    {showTextLogo && <LogoFallback>Pathways</LogoFallback>}
                    <HeaderTitle>Passwort zurücksetzen</HeaderTitle>
                    <HeaderEmail className="text-muted small mb-0">
                        Für <strong>{email || "phone1@test.de"}</strong>
                    </HeaderEmail>
                </Header>

                <Body className="p-4">
                    {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

                    <form onSubmit={handleReset}>
                        <div className="mb-3">
                            <label className="form-label">Neues Passwort</label>
                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    placeholder="Mindestens 12 Zeichen"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={12}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword((s) => !s)}
                                    aria-label="Toggle password visibility"
                                >
                                    {showPassword ? "Verbergen" : "Anzeigen"}
                                </button>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Passwort bestätigen</label>
                            <div className="input-group">
                                <input
                                    type={showConfirm ? "text" : "password"}
                                    className="form-control"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    minLength={12}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowConfirm((s) => !s)}
                                    aria-label="Toggle confirm password visibility"
                                >
                                    {showConfirm ? "Verbergen" : "Anzeigen"}
                                </button>
                            </div>
                        </div>

                        <div className="mb-3">
                            <small className="text-muted">Passwort-Anforderungen:</small>
                            <RequirementsList className="small mb-2">
                                <ReqItem ok={req.length}>{req.length ? "✓" : "◻︎"} mindestens zwölf Zeichen</ReqItem>
                                <ReqItem ok={req.upper}>
                                    {req.upper ? "✓" : "◻︎"} mindestens einem Großbuchstabe
                                </ReqItem>
                                <ReqItem ok={req.number}>{req.number ? "✓" : "◻︎"} mindestens einer Zahl</ReqItem>
                                <ReqItem ok={req.special}>
                                    {req.special ? "✓" : "◻︎"} darf nur folgende Sonderzeichen enthalten: ?,!,_,*,.,@
                                </ReqItem>
                            </RequirementsList>
                        </div>

                        <button type="submit" className="btn btn-primary w-100">
                            Passwort aktualisieren
                        </button>
                    </form>
                </Body>
            </CardWrapper>
        </PageWrapper>
    );
};

export default ResetPasswordCustomisedScreen;

// Styled components (no inline CSS)
const PageWrapper = styled.div`
    min-height: 80vh;
    background: linear-gradient(135deg, #f6f9ff 0%, #ffffff 50%);
    padding: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CardWrapper = styled.div`
    max-width: 480px;
    width: 100%;
    border-radius: 12px;
`;

const Header = styled.div`
    text-align: center;
    padding: 1.25rem 1rem;
    border-bottom: 1px solid #eef2ff;
`;

const Logo = styled.img`
    height: 48px;
    object-fit: contain;
    margin-bottom: 8px;
`;

const LogoFallback = styled.div`
    font-weight: 700;
    font-size: 18px;
    color: #0f172a;
    margin-bottom: 6px;
`;

const HeaderTitle = styled.h3`
    margin: 0;
    font-size: 18px;
`;

const HeaderEmail = styled.p`
    margin: 0;
`;

const Body = styled.div``;

const RequirementsList = styled.ul`
    list-style: none;
    padding-left: 0;
    margin-bottom: 0.5rem;
`;

const ReqItem = styled.li`
    color: ${(p) => (p.ok ? "green" : "#666")};
`;
